/**
 * Email theme presets + the default theme for scheduled-automation EMAIL
 * delivery (delivery.kind 'email' / 's3+email'). The sibling of report-themes.ts
 * for the email-template builder: the studio preview (email-doc.ts), and —
 * mirrored in Python — the backend delivery template (renderer/_templates_async.py)
 * both resolve meta.email.css through here.
 *
 * Storage contract (meta.email.css):
 *   - empty / undefined → the built-in DEFAULT_EMAIL_CSS (so the backend only
 *     needs the default for the common case, and every scheduled email is
 *     WatchGuard-branded with no creator action).
 *   - any other string  → stored verbatim and inlined; a preset writes its full
 *     CSS here, "Advanced" lets the creator hand-edit it.
 *
 * Email clients strip/rewrite CSS unevenly, so the theme sticks to a single
 * centered card, table-safe rules, web-safe fonts and inline-friendly colors —
 * no flexbox/grid, no external fonts, and NO CSS custom properties (var()) —
 * desktop Outlook renders through Word's engine, which supports none of those.
 * These agents email WatchGuard employees (mostly Outlook, some Gmail), so
 * every color below is a literal hex value repeated per rule rather than a
 * :root variable, even though that means restating "#d81e2c" a few times.
 */

/** The default WatchGuard email theme. Keep in sync with the Python copy in
 *  renderer/_templates_async.py (EMAIL_CSS). Colors (repeat these literals,
 *  not variables — see the file header): ink #1a1d23, muted #6b7280,
 *  line #e3e6eb, accent #d81e2c, accent-dark #b3121f, soft #f3f4f6,
 *  page #f0f1f4. */
export const DEFAULT_EMAIL_CSS = `
  body { margin: 0; padding: 0; background: #f0f1f4;
         font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
         color: #1a1d23; line-height: 1.6; font-size: 15px;
         -webkit-text-size-adjust: 100%; }
  .eml-wrap { width: 100%; background: #f0f1f4; padding: 24px 0; }
  .eml-card { max-width: 640px; margin: 0 auto; background: #fff;
              border: 1px solid #e3e6eb; border-radius: 10px; overflow: hidden;
              box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
  .eml-head { background: #ffffff; padding: 16px 28px 14px; border-bottom: 3px solid #d81e2c; }
  .eml-logo { height: 22px; width: auto; vertical-align: middle; }
  .eml-brand-sub { color: #6b7280; font-weight: 600; font-size: 11px; vertical-align: middle;
                   margin-left: 10px; text-transform: uppercase; letter-spacing: 0.08em; }
  .eml-body { padding: 26px 28px; }
  .eml-body h1 { font-size: 21px; margin: 0 0 0.5em; line-height: 1.25; font-weight: 650; }
  .eml-body h2 { font-size: 17px; margin: 1.3em 0 0.4em; padding-bottom: 4px; font-weight: 650;
                 border-bottom: 1px solid #e3e6eb; color: #b3121f; }
  .eml-body h3 { font-size: 15px; margin: 1.1em 0 0.3em; font-weight: 650; }
  .eml-body p { margin: 0.6em 0; }
  .eml-body ul, .eml-body ol { margin: 0.6em 0; padding-left: 1.4em; }
  .eml-body li { margin: 0.25em 0; }
  .eml-body a { color: #b3121f; }
  .eml-body strong { font-weight: 680; }
  .eml-body table { border-collapse: collapse; width: 100%; margin: 1em 0; font-size: 13.5px; }
  .eml-body th, .eml-body td { border: 1px solid #e3e6eb; padding: 7px 10px; text-align: left;
                               vertical-align: top; }
  .eml-body thead th, .eml-body table tr:first-child th { background: #2b2f38; color: #fff;
                                                          font-weight: 600; }
  .eml-body tbody tr:nth-child(even), .eml-body table tr:nth-child(even) { background: #f3f4f6; }
  .eml-body blockquote { margin: 1em 0; padding: 8px 14px; border-left: 4px solid #d81e2c;
                         background: #f3f4f6; border-radius: 0 6px 6px 0; }
  .eml-body code { background: #f3f4f6; padding: 1.5px 5px; border-radius: 4px; font-size: 0.9em;
                   font-family: "SF Mono", Consolas, monospace; }
  .eml-body hr { border: 0; border-top: 1px solid #e3e6eb; margin: 1.4em 0; }
  .eml-btn { display: inline-block; background: #d81e2c; color: #fff !important;
             text-decoration: none; font-weight: 600; font-size: 14px; padding: 10px 20px;
             border-radius: 6px; margin: 6px 0; }
  .eml-foot { padding: 16px 28px; border-top: 1px solid #e3e6eb; background: #f3f4f6;
              color: #6b7280; font-size: 12px; }
  .eml-foot a { color: #6b7280; }`;

// Preset variations are small overrides appended to the default so they inherit
// every base rule and only restate what changes (mirrors report-themes.ts).
// No :root vars here either — each override restates literal colors, same
// reason as DEFAULT_EMAIL_CSS above.
const COMPACT_OVERRIDE = `
  /* Compact */
  .eml-body { padding: 18px 20px; font-size: 13.5px; }
  .eml-head { padding: 12px 20px 10px; }
  .eml-body h1 { font-size: 18px; }
  .eml-body h2 { font-size: 15px; }
  .eml-body p, .eml-body li { margin: 0.35em 0; }
  .eml-foot { padding: 12px 20px; }`;

const MINIMAL_OVERRIDE = `
  /* Minimal — quiet, monochrome, no card chrome */
  .eml-card { border-color: #e5e7eb; box-shadow: none; border-radius: 0; }
  .eml-head { border-bottom: 1px solid #d1d5db; }
  .eml-body h2 { color: #111; border-bottom: 0; }
  .eml-body blockquote { border-left-color: #111; }
  .eml-body thead th, .eml-body table tr:first-child th { background: #f3f4f6; color: #111; }
  .eml-btn { background: #111; }`;

const BOLD_OVERRIDE = `
  /* Bold — vivid red accents throughout (header stays white so the logo reads) */
  .eml-head { border-bottom: 4px solid #d81e2c; }
  .eml-body h2 { color: #fff; background: #d81e2c; border: 0; padding: 6px 12px;
                 border-radius: 5px; }`;

export type EmailThemeKey = 'default' | 'compact' | 'minimal' | 'bold';

/** Preset key → display label for the picker. */
export const EMAIL_PRESETS: { key: EmailThemeKey; label: string }[] = [
  { key: 'default', label: 'Default' },
  { key: 'compact', label: 'Compact' },
  { key: 'minimal', label: 'Minimal' },
  { key: 'bold', label: 'Bold' },
];

/** The full CSS a preset stores in meta.email.css. 'default' stores empty so the
 *  built-in theme (UI + backend) stays the single source of truth. */
export function emailPresetCss(key: EmailThemeKey): string {
  switch (key) {
    case 'compact':
      return DEFAULT_EMAIL_CSS + COMPACT_OVERRIDE;
    case 'minimal':
      return DEFAULT_EMAIL_CSS + MINIMAL_OVERRIDE;
    case 'bold':
      return DEFAULT_EMAIL_CSS + BOLD_OVERRIDE;
    default:
      return '';
  }
}

/** Which preset (if any) a stored css string corresponds to — drives the radio
 *  selection. Returns 'custom' when the css was hand-edited. */
export function emailThemeKeyForCss(css: string | undefined): EmailThemeKey | 'custom' {
  const c = (css ?? '').trim();
  if (!c) return 'default';
  for (const { key } of EMAIL_PRESETS) {
    if (key !== 'default' && emailPresetCss(key).trim() === c) return key;
  }
  return 'custom';
}

/**
 * Strip the handful of CSS constructs that could break out of the inlined
 * <style> or fetch/execute external resources. Creators are trusted WG
 * employees, so this is a foot-gun guard, not a hostile-input defense (mirrors
 * sanitizeReportCss): blocks any `<` (`</style>` breakout / HTML injection),
 * `javascript:` URLs, IE `expression(...)`, and `@import` (remote stylesheets).
 */
export function sanitizeEmailCss(css: string): string {
  return (css || '')
    .replace(/</g, '')
    .replace(/javascript:/gi, '')
    .replace(/expression\s*\(/gi, '')
    .replace(/@import/gi, '')
    .slice(0, 20000);
}

/** Resolve meta.email.css to the CSS to inline: sanitized custom css, or the
 *  built-in default when empty. */
export function resolveEmailCss(css: string | undefined): string {
  const c = sanitizeEmailCss((css ?? '').trim());
  return c || DEFAULT_EMAIL_CSS;
}
